import { fetchJSON as fetchAPI } from './client';

export type CommercialDashboard = {
  tenant_id: string;
  total_skus: number;
  total_revenue: number;
  total_gross_profit: number;
  total_net_profit: number;
  avg_margin_pct: number;
  avg_net_margin_pct: number;
  unprofitable_skus: number;
  buy_box_win_rate_pct: number;
  active_recommendations: number;
  active_promotions: number;
  margin_erosion_count: number;
  high_potential_count: number;
  forecast_accuracy_pct: number;
};

export type PricingRecord = {
  id: string;
  sku: string;
  asin?: string;
  selling_price: number;
  buy_box_price?: number;
  list_price?: number;
  promotional_price?: number;
  margin_pct: number;
  price_elasticity?: number;
  competitive_gap_pct?: number;
};

export type ProfitabilityRecord = {
  id: string;
  sku: string;
  asin?: string;
  revenue: number;
  gross_profit: number;
  net_profit: number;
  contribution_margin: number;
  contribution_after_ads: number;
  advertising_cost: number;
  marketplace_fees: number;
  margin_pct: number;
  net_margin_pct: number;
  unprofitable: boolean;
};

export type MarginAnalysis = {
  id: string;
  sku: string;
  gross_margin_pct: number;
  net_margin_pct: number;
  margin_trend?: string;
  erosion_risk: string;
  fee_burden_pct: number;
  ad_burden_pct: number;
};

export type BuyBoxIntel = {
  id: string;
  asin: string;
  sku?: string;
  owns_buy_box: boolean;
  buy_box_percentage: number;
  price_competitiveness: number;
  offer_position: number;
};

export type ElasticityModel = {
  id: string;
  sku: string;
  demand_elasticity: number;
  revenue_elasticity: number;
  profit_elasticity: number;
  conversion_elasticity: number;
};

export type CommercialRecommendation = {
  id: string;
  type: string;
  title: string;
  description?: string;
  evidence?: string;
  sku?: string;
  current_price?: number;
  suggested_price?: number;
  expected_profit_impact?: number;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
};

export type PromotionPlan = {
  id: string;
  name: string;
  type: string;
  sku?: string;
  discount_pct?: number;
  expected_revenue?: number;
  expected_profit?: number;
  status?: string;
};

export type PortfolioRecord = {
  id: string;
  sku: string;
  action: string;
  title: string;
  rationale?: string;
  revenue?: number;
  net_profit?: number;
  margin_pct?: number;
  priority_score: number;
};

export type ScenarioRecord = {
  id: string;
  name: string;
  type: string;
  outcome?: string;
  revenue_impact?: number;
  profit_impact?: number;
  orders_impact?: number;
  created_at: string;
};

const API_BASE = import.meta.env.VITE_PRICINGINTEL_API ?? '/api/v1/pricingintel';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

// Re-exported for existing call sites; formatting lives in lib/currency (₹, en-IN).
export { fmtINR, fmtINR as fmtUSD } from '../lib/currency';

export const pricingintelApi = {
  dashboard: () => fetchJSON<CommercialDashboard>('/dashboard'),
  listPricing: () => fetchJSON<PricingRecord[]>('/pricing'),
  listProfitability: () => fetchJSON<ProfitabilityRecord[]>('/profitability'),
  listMargins: () => fetchJSON<MarginAnalysis[]>('/margin'),
  listBuyBox: () => fetchJSON<BuyBoxIntel[]>('/buybox'),
  listElasticity: () => fetchJSON<ElasticityModel[]>('/elasticity'),
  listPortfolio: () => fetchJSON<PortfolioRecord[]>('/portfolio'),
  listRecommendations: () => fetchJSON<CommercialRecommendation[]>('/recommendations'),
  generateRecommendations: () => fetchJSON<CommercialRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  approveRecommendation: (id: string) =>
    fetchJSON<CommercialRecommendation>(`/recommendations/${id}/approve`, { method: 'POST', body: JSON.stringify({}) }),
  listPromotions: () => fetchJSON<PromotionPlan[]>('/promotions'),
  createPromotion: (body: { name: string; type: string; sku?: string; discount_pct?: number }) =>
    fetchJSON<PromotionPlan>('/promotions', { method: 'POST', body: JSON.stringify(body) }),
  listScenarios: () => fetchJSON<ScenarioRecord[]>('/scenarios'),
  runScenario: (body: { name: string; type: string }) =>
    fetchJSON<ScenarioRecord>('/scenarios', { method: 'POST', body: JSON.stringify(body) }),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
